import { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import Container from '@/components/Container';
import useIsTablet from '@/hooks/useIsTablet';
import Button from '@/components/Button';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { ResourceCard } from '@/components/ResourceCard';
import { useResourceHubList } from './hooks/useResourceHubList';
import { useResourceHubPage } from './hooks/useResourceHubPage';
import { useResourceHubListStore } from './store/useResourceHubListStore';
import { ResourceHubFilters } from './components/filters';
import { ResourceHubListLoading } from './components/loading';
import PageHeader from '@/components/PageHeader';
import { cn } from '@/components/ui/utils';

const ResourceHubListPageContent = () => {
  const { isTablet } = useIsTablet();
  const { loading, loadMore, hasMore, shouldFetch } = useResourceHubList();
  const { pageContent } = useResourceHubPage();
  const resources = useResourceHubListStore((state) => state.resources);
  const searchQuery = useResourceHubListStore((state) => state.searchQuery);
  const selectedCategoryIds = useResourceHubListStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useResourceHubListStore((state) => state.selectedSubcategoryIds);

  const title = pageContent?.title ?? 'Resource Hub';
  const description = pageContent?.description;
  const cardLinkText = pageContent?.cardLinkText ?? 'Go to resource';
  const noResultsText = pageContent?.noResultsText ?? 'No resources found';
  const loadMoreText = pageContent?.loadMoreText ?? 'More resources';
  const resultsTitle = pageContent?.resultsTitle ?? 'Your results';
  const hasActiveFilters = searchQuery !== '' || selectedCategoryIds.length > 0 || selectedSubcategoryIds.length > 0;

  return (
    <>
      <PageHeader title={title ?? 'Resource Hub'} description={description} />
      <Container className="min-h-40" addMargin={!isTablet}>
        <div className="col-span-12 mt-[0px]">
          <ResourceHubFilters />

          {hasActiveFilters && <p className="my-[32px] font-haffersq text-sh-24 text-dark-grey">{resultsTitle}</p>}

          <div
            className={cn(
              'grid grid-cols-1 gap-x-[25px] gap-y-[16px] overflow-visible md:grid-cols-2 lg:grid-cols-2',
              !hasActiveFilters && 'mt-[32px]',
            )}>
            {resources.map((resource) => (
              <AnimatedFadeInComponent key={resource.documentId} className="h-full overflow-visible">
                <ResourceCard resource={resource} linkText={cardLinkText} isResourceHub />
              </AnimatedFadeInComponent>
            ))}
          </div>

          {resources.length === 0 && !loading && shouldFetch && (
            <div className="py-sh-60 text-center">
              <p className="font-cadiz text-sh-16-24 text-gray-500">{noResultsText}</p>
            </div>
          )}

          {hasMore && (
            <div className="mt-sh-30 flex justify-start">
              <Button theme="solid" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : loadMoreText}
              </Button>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export const ResourceHubListPage = () => {
  const location = useLocation();

  return (
    <Suspense key={location.pathname} fallback={<ResourceHubListLoading />}>
      <ResourceHubListPageContent />
    </Suspense>
  );
};
